import { AlertTriangle } from 'lucide-react';
import React from 'react';

import { Modal } from '@/components/Modal';
import { PartialAsset } from '@/hooks/useAssets';
import { AssetStatus, AssetStatusLabel } from '@/types';

/**
 * Props for StatusConfirmationModal component
 */
export interface StatusConfirmationModalProps {
  /** Current confirmation state with assets and target status */
  confirmStatus: {
    data: PartialAsset[];
    status: AssetStatus;
  } | null;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when status change is confirmed */
  onConfirm: (data: PartialAsset[], status: AssetStatus) => Promise<void>;
  /** Whether the current user is a Praetorian user (optional, defaults to true) */
  isPraetorianUser?: boolean;
}

/**
 * Status Confirmation Modal Component
 *
 * Displays a confirmation dialog when the user attempts to change the status
 * of one or more assets. Shows the list of affected assets and the target status.
 *
 * @param props - Component props
 * @returns Status confirmation modal
 *
 * @example
 * ```tsx
 * <StatusConfirmationModal
 *   confirmStatus={confirmStatus}
 *   onClose={() => setConfirmStatus(null)}
 *   onConfirm={async (data, status) => {
 *     await handleUpdateStatus(data, status);
 *     invalidateAssets();
 *   }}
 * />
 * ```
 */
export const StatusConfirmationModal: React.FC<
  StatusConfirmationModalProps
> = ({ confirmStatus, onClose, onConfirm, isPraetorianUser = true }) => {
  // Show warning when non-Praetorian user is freezing assets
  const showFreezeWarning =
    !isPraetorianUser && confirmStatus?.status === AssetStatus.Frozen;

  return (
    <Modal
      title="Confirm Change Status"
      open={!!confirmStatus}
      onClose={onClose}
      footer={{
        text: 'Confirm',
        onClick: async () => {
          if (!confirmStatus) return;
          const { data, status } = confirmStatus;
          await onConfirm(data, status);
        },
      }}
    >
      <div className="pb-4">
        {confirmStatus &&
          `Are you sure you want to update the status of following assets to "${AssetStatusLabel[confirmStatus.status as AssetStatus]}"?`}
      </div>
      {showFreezeWarning && (
        <div className="mb-4 flex items-start gap-3 rounded-md border border-yellow-500/20 bg-yellow-500/10 p-3 text-sm text-yellow-600 dark:text-yellow-400">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <p className="font-medium">Warning: Freezing is irreversible</p>
            <p className="mt-1 opacity-80">
              Once frozen, you will not be able to unfreeze these assets.
              Contact your Praetorian MSP if you need to restore frozen assets.
            </p>
          </div>
        </div>
      )}
      <div className="scrollbar-dark max-h-[300px] overflow-auto">
        {confirmStatus?.data.map(asset => (
          <div key={asset.identifier}>{asset.identifier}</div>
        ))}
      </div>
    </Modal>
  );
};
